import type {
  TelemetryAggregatePointDto,
  TelemetryAggregatesResponseDto,
  TelemetryDto,
} from "@/lib/api/types";

function hashToUnitInterval(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (Math.imul(31, h) + seed.charCodeAt(i)) | 0;
  }
  return ((h >>> 0) % 10_000) / 10_000;
}

/** Deterministic “noise” per bucket + metric. */
function metricValue(deviceId: string, metric: string, tMs: number): number {
  const phase = hashToUnitInterval(`${deviceId}:${metric}`) * Math.PI * 2;
  const hour = tMs / 3_600_000;
  const base =
    metric === "temperature" || metric === "temp"
      ? 22
      : metric === "humidity" || metric === "rh"
        ? 55
        : metric === "pressure" || metric === "pressure_kpa"
          ? 101.3
          : 10 + hashToUnitInterval(`${deviceId}:${metric}`) * 20;
  const wave = Math.sin(hour / 6 + phase) * (metric.includes("pressure") ? 0.4 : 2);
  const drift = Math.sin(hour / 48 + phase * 0.3) * 1.5;
  return Math.round((base + wave + drift) * 100) / 100;
}

export function generateAggregatePoints(
  deviceId: string,
  start: Date,
  end: Date,
  bucketSeconds: number,
  metrics: string[],
  maxPoints: number,
): TelemetryAggregatesResponseDto {
  const startMs = start.getTime();
  const endMs = end.getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs || metrics.length === 0) {
    return { bucket_seconds: bucketSeconds, metrics, points: [] };
  }

  const bucketMs = Math.max(30, bucketSeconds) * 1000;
  const points: TelemetryAggregatePointDto[] = [];
  for (let t = startMs; t <= endMs && points.length < maxPoints; t += bucketMs) {
    const values: Record<string, number | null> = {};
    for (const m of metrics) {
      const key = m.trim();
      if (!key) {
        continue;
      }
      values[key] = metricValue(deviceId, key, t);
    }
    points.push({ t: new Date(t).toISOString(), values });
  }

  return {
    bucket_seconds: bucketSeconds,
    metrics,
    points,
  };
}

export function generateTelemetryRows(
  deviceId: string,
  start: Date,
  end: Date,
  stepMs: number,
  metrics: string[],
  maxRows: number,
): TelemetryDto[] {
  const out: TelemetryDto[] = [];
  const startMs = start.getTime();
  const endMs = end.getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return out;
  }
  for (let t = startMs; t <= endMs && out.length < maxRows; t += stepMs) {
    const data: Record<string, number> = {};
    for (const m of metrics) {
      const key = m.trim();
      if (key) {
        data[key] = metricValue(deviceId, key, t);
      }
    }
    out.push({
      time: new Date(t).toISOString(),
      device_id: deviceId,
      data,
    });
  }
  return out;
}

/** Single live point for buffers / hooks. */
export function generateLiveTelemetryPoint(
  deviceId: string,
  metricKeys: string[],
  tMs = Date.now(),
): Record<string, number> {
  const data: Record<string, number> = {};
  for (const k of metricKeys) {
    const key = k.trim();
    if (key) {
      const v = metricValue(deviceId, key, tMs);
      const jitter = (Math.random() - 0.5) * 0.15 * (v || 1);
      data[key] = Math.round((v + jitter) * 1000) / 1000;
    }
  }
  return data;
}
